/**
 * Validates an IBAN (International Bank Account Number) using the MOD-97 algorithm.
 *
 * Algorithm (ISO 7064):
 * 1. Strip spaces, validate basic format (2 letter country code + 2 check digits + BBAN)
 * 2. Verify length matches expected length for the country
 * 3. Move the first 4 characters to the end of the string
 * 4. Replace each letter with two digits (A=10, B=11, ..., Z=35)
 * 5. Compute the remainder of the resulting number divided by 97
 * 6. If the remainder is 1, the IBAN is valid
 *
 * @param iban - IBAN string (spaces and "IBAN" prefix are tolerated).
 *               If the input has no country code (starts with digits),
 *               the defaultCountryCode is prepended automatically.
 * @param defaultCountryCode - Country code to prepend when missing (default: "PL")
 * @returns true if the IBAN is valid
 */
export function validateIBAN(
  iban: string,
  defaultCountryCode: string = "PL"
): boolean {
    // Strip optional "IBAN" prefix, spaces, and hyphens; uppercase
    let cleaned = iban
        .replace(/^IBAN\s*/i, "")
        .replace(/[\s-]/g, "")
        .toUpperCase();

    // If the input starts with digits (no country code), prepend the default
    if (/^\d/.test(cleaned)) {
        cleaned = defaultCountryCode.toUpperCase() + cleaned;
    }

    // Basic format: 2 letters + 2 digits + up to 30 alphanumeric characters
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/.test(cleaned)) {
        return false;
    }

    // Check country-specific length
    const countryCode = cleaned.slice(0, 2);
    const expectedLength = IBAN_LENGTHS[countryCode];

    if (expectedLength === undefined) {
        return false; // Unknown country code
    }

    if (cleaned.length !== expectedLength) {
        return false;
    }

    // MOD-97 validation (ISO 7064)
    // 1. Move first 4 characters to the end
    const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);

    // 2. Replace letters with numeric equivalents (A=10 .. Z=35)
    const numericString = rearranged.replace(/[A-Z]/g, (char) =>
        (char.charCodeAt(0) - 55).toString()
    );

    // 3. Compute mod 97 using piecewise arithmetic (number can be 70+ digits)
    const remainder = mod97(numericString);

    return remainder === 1;
}

/**
 * Computes number mod 97 for an arbitrarily long numeric string.
 * Processes the string in chunks to avoid BigInt or floating-point issues.
 */
function mod97(numericString: string): number {
  let remainder = 0;

  for (let i = 0; i < numericString.length; i++) {
    remainder = (remainder * 10 + parseInt(numericString[i], 10)) % 97;
  }

  return remainder;
}

/**
 * IBAN lengths per country code.
 * Source: SWIFT IBAN Registry / iban.com (October 2025)
 */
const IBAN_LENGTHS: Record<string, number> = {
  AD: 24, // Andorra
  AE: 23, // United Arab Emirates
  AL: 28, // Albania
  AO: 25, // Angola
  AT: 20, // Austria
  AZ: 28, // Azerbaijan
  BA: 20, // Bosnia and Herzegovina
  BE: 16, // Belgium
  BF: 28, // Burkina Faso
  BG: 22, // Bulgaria
  BH: 22, // Bahrain
  BI: 27, // Burundi
  BJ: 28, // Benin
  BR: 29, // Brazil
  BY: 28, // Belarus
  CF: 27, // Central African Republic
  CG: 27, // Congo
  CH: 21, // Switzerland
  CI: 28, // Ivory Coast
  CM: 27, // Cameroon
  CR: 22, // Costa Rica
  CV: 25, // Cape Verde
  CY: 28, // Cyprus
  CZ: 24, // Czech Republic
  DE: 22, // Germany
  DJ: 27, // Djibouti
  DK: 18, // Denmark
  DO: 28, // Dominican Republic
  DZ: 26, // Algeria
  EE: 20, // Estonia
  EG: 29, // Egypt
  ES: 24, // Spain
  FI: 18, // Finland
  FK: 18, // Falkland Islands
  FO: 18, // Faroe Islands
  FR: 27, // France
  GA: 27, // Gabon
  GB: 22, // United Kingdom
  GE: 22, // Georgia
  GI: 23, // Gibraltar
  GL: 18, // Greenland
  GQ: 27, // Equatorial Guinea
  GR: 27, // Greece
  GT: 28, // Guatemala
  GW: 25, // Guinea-Bissau
  HN: 28, // Honduras
  HR: 21, // Croatia
  HU: 28, // Hungary
  IE: 22, // Ireland
  IL: 23, // Israel
  IQ: 23, // Iraq
  IR: 26, // Iran
  IS: 26, // Iceland
  IT: 27, // Italy
  JO: 30, // Jordan
  KM: 27, // Comoros
  KW: 30, // Kuwait
  KZ: 20, // Kazakhstan
  LB: 28, // Lebanon
  LC: 32, // Saint Lucia
  LI: 21, // Liechtenstein
  LT: 20, // Lithuania
  LU: 20, // Luxembourg
  LV: 21, // Latvia
  LY: 25, // Libya
  MA: 28, // Morocco
  MC: 27, // Monaco
  MD: 24, // Moldova
  ME: 22, // Montenegro
  MG: 27, // Madagascar
  MK: 19, // North Macedonia
  ML: 28, // Mali
  MN: 20, // Mongolia
  MR: 27, // Mauritania
  MT: 31, // Malta
  MU: 30, // Mauritius
  MZ: 25, // Mozambique
  NE: 28, // Niger
  NI: 28, // Nicaragua
  NL: 18, // Netherlands
  NO: 15, // Norway
  OM: 23, // Sultanate of Oman
  PK: 24, // Pakistan
  PL: 28, // Poland
  PS: 29, // Palestine
  PT: 25, // Portugal
  QA: 29, // Qatar
  RO: 24, // Romania
  RS: 22, // Serbia
  RU: 33, // Russia
  SA: 24, // Saudi Arabia
  SC: 31, // Seychelles
  SD: 18, // Sudan
  SE: 24, // Sweden
  SI: 19, // Slovenia
  SK: 24, // Slovakia
  SM: 27, // San Marino
  SN: 28, // Senegal
  SO: 23, // Somalia
  ST: 25, // Sao Tome and Principe
  SV: 28, // El Salvador
  TD: 27, // Chad
  TG: 28, // Togo
  TL: 23, // Timor-Leste
  TN: 24, // Tunisia
  TR: 26, // Turkey
  UA: 29, // Ukraine
  VA: 22, // Vatican City
  VG: 24, // British Virgin Islands
  XK: 20, // Kosovo
  YE: 30, // Yemen
};